// ---------------------------------------------------------------------------
// useDiscoveryFilterModals — Modal orchestration for filter/persona flows
// ---------------------------------------------------------------------------
// Manages the 3-step Add Filter flow (DimensionPicker → ValuePicker → Apply)
// and the Load Persona flow. All modal visibility, dimension value loading,
// and persona CRUD are encapsulated here.
// ---------------------------------------------------------------------------

import { useState, useMemo, useCallback } from 'react'
import { usePersonas } from '@/hooks/usePersonas'
import { funnelService } from '@/services/v11/funnelService'
import { getValuesForDimension, resolveValueLabels } from './getValuesForDimension'
import type { DimensionConfig } from '@/components/v11/funnel/constants'
import type { ValueOption } from '@/components/v11/funnel/modals/ValuePickerModal'
import type { DiscoveryFilter } from '@/lib/discovery/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseDiscoveryFilterModalsOptions {
  readonly filterOptions: Record<string, unknown> | undefined
  readonly addFilter: (dimension: DimensionConfig, values: readonly (string | number)[], labels: readonly string[], platforms?: readonly string[]) => void
  readonly loadFilters: (filters: readonly DiscoveryFilter[]) => void
  readonly urlFilters: readonly DiscoveryFilter[]
  readonly dateRange?: { date_from?: string; date_to?: string }
}

export interface UseDiscoveryFilterModalsResult {
  // Load Persona modal
  readonly isLoadPersonaOpen: boolean
  readonly personas: ReadonlyArray<{ id: number; name: string; layers: ReadonlyArray<Record<string, unknown>> }>
  readonly isLoadingPersonas: boolean
  readonly isDeletingPersona: boolean
  readonly openLoadPersona: () => void
  readonly closeLoadPersona: () => void
  readonly selectPersona: (persona: { layers?: ReadonlyArray<{ dimension: string; dimensionLabel?: string; values: (string | number)[]; valueLabels: string[] }> }) => void
  readonly deletePersona: (id: number) => Promise<void>
  // Dimension/Value picker modals
  readonly isDimensionModalOpen: boolean
  readonly isValueModalOpen: boolean
  readonly selectedDimension: DimensionConfig | null
  readonly dimensionValues: readonly ValueOption[]
  readonly isLoadingValues: boolean
  readonly isSearchMode: boolean
  readonly isSearching: boolean
  readonly usedDimensions: readonly string[]
  readonly initialSelectedValues: readonly (string | number)[]
  readonly openAddFilter: () => void
  readonly selectDimension: (dim: DimensionConfig) => Promise<void>
  readonly applyValues: (values: readonly (string | number)[], labels: readonly string[]) => void
  readonly closeValueModal: () => void
  readonly closeDimensionModal: () => void
  readonly handleSearch: (query: string) => void
  readonly requestDimensionCounts: () => Promise<undefined>
}

// ---------------------------------------------------------------------------
// Search-mode dimension keys (require API calls to load values)
// ---------------------------------------------------------------------------

const SEARCH_DIMENSIONS = new Set(['channel', 'hashtag', 'post', 'brand', 'team', 'item'])

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDiscoveryFilterModals(
  options: UseDiscoveryFilterModalsOptions,
): UseDiscoveryFilterModalsResult {
  const { filterOptions, addFilter, loadFilters, urlFilters, dateRange } = options

  // Persona management (unified API)
  const { personas, isLoading: isLoadingPersonas, deletePersona: doDeletePersona } = usePersonas()

  // Modal visibility state
  const [isLoadPersonaOpen, setIsLoadPersonaOpen] = useState(false)
  const [isDimensionModalOpen, setIsDimensionModalOpen] = useState(false)
  const [isValueModalOpen, setIsValueModalOpen] = useState(false)

  // Dimension/value picker state
  const [selectedDimension, setSelectedDimension] = useState<DimensionConfig | null>(null)
  const [dimensionValues, setDimensionValues] = useState<readonly ValueOption[]>([])
  const [isLoadingValues, setIsLoadingValues] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isDeletingPersona, setIsDeletingPersona] = useState(false)

  // Derived: is the current dimension a search-mode dimension?
  const isSearchMode = selectedDimension !== null && SEARCH_DIMENSIONS.has(selectedDimension.key)

  // Derived: which dimensions are already active (for visual indicator in picker)
  const usedDimensions = useMemo(
    () => urlFilters.map((f) => f.dimension),
    [urlFilters],
  )

  // Derived: pre-selected values for the currently selected dimension (when editing existing filter)
  const initialSelectedValues = useMemo(() => {
    if (!selectedDimension) return [] as readonly (string | number)[]
    const existingFilter = urlFilters.find((f) => f.dimension === selectedDimension.key)
    return existingFilter ? existingFilter.values : ([] as readonly (string | number)[])
  }, [selectedDimension, urlFilters])

  // -------------------------------------------------------------------------
  // Add Filter flow
  // -------------------------------------------------------------------------

  const openAddFilter = useCallback(() => {
    setIsDimensionModalOpen(true)
  }, [])

  const selectDimension = useCallback(async (dimension: DimensionConfig) => {
    setSelectedDimension(dimension)
    setIsDimensionModalOpen(false)
    setIsValueModalOpen(true)

    if (SEARCH_DIMENSIONS.has(dimension.key)) {
      setIsLoadingValues(true)
      try {
        let results: ValueOption[] = []

        if (dimension.key === 'channel') {
          const data = await funnelService.searchChannels('', 30)
          results = data.map(r => ({
            value: r.id,
            label: r.name.toLowerCase(),
            platform: r.platform,
            count: r.postsCount,
          }))
        } else if (dimension.key === 'hashtag') {
          const data = await funnelService.searchHashtags('', 30, dateRange)
          results = data.map(r => ({
            value: r.id,
            label: `${r.name} (${r.postsCount.toLocaleString()} posts)`,
          }))
        } else if (dimension.key === 'post') {
          const data = await funnelService.searchPosts('', 30, dateRange)
          results = data.map(r => ({
            value: r.id,
            label: `${r.caption} — @${r.author}`,
          }))
        } else if (dimension.key === 'brand') {
          const data = await funnelService.searchBrands('')
          results = data.map(r => ({ value: r.id, label: r.name }))
        } else if (dimension.key === 'team') {
          const data = await funnelService.searchBrands('') // Teams use same endpoint
          results = data.map(r => ({ value: r.id, label: r.name }))
        } else if (dimension.key === 'item') {
          const data = await funnelService.searchItems('')
          results = data.map(r => ({ value: r.id, label: r.name }))
        }

        setDimensionValues(results)
      } catch {
        setDimensionValues([])
      } finally {
        setIsLoadingValues(false)
      }
    } else {
      const values = getValuesForDimension(dimension.key, filterOptions)
      setDimensionValues(values)
    }
  }, [filterOptions, dateRange])

  const applyValues = useCallback((
    values: readonly (string | number)[],
    valueLabels: readonly string[],
  ) => {
    if (!selectedDimension) return

    // For channel filters, look up platform info from dimension values
    let platforms: string[] | undefined
    if (selectedDimension.key === 'channel' && dimensionValues.length > 0) {
      const valueMap = new Map(dimensionValues.map(v => [v.value, v.platform]))
      platforms = values.map(v => valueMap.get(v) || '').filter(Boolean) as string[]
      if (platforms.length === 0) platforms = undefined
    }

    addFilter(selectedDimension, values, valueLabels, platforms)
    setIsValueModalOpen(false)
    setSelectedDimension(null)
  }, [selectedDimension, addFilter, dimensionValues])

  const closeValueModal = useCallback(() => {
    setIsValueModalOpen(false)
    setSelectedDimension(null)
    setDimensionValues([])
    setIsLoadingValues(false)
    setIsSearching(false)
  }, [])

  const handleSearch = useCallback(async (query: string) => {
    if (!selectedDimension) return
    setIsSearching(true)
    try {
      let results: ValueOption[] = []
      if (selectedDimension.key === 'channel') {
        const data = await funnelService.searchChannels(query, 30)
        results = data.map(r => ({
          value: r.id,
          label: r.name.toLowerCase(),
          platform: r.platform,
          count: r.postsCount,
        }))
      } else if (selectedDimension.key === 'hashtag') {
        const data = await funnelService.searchHashtags(query, 30, dateRange)
        results = data.map(r => ({
          value: r.id,
          label: `${r.name} (${r.postsCount.toLocaleString()} posts)`,
        }))
      } else if (selectedDimension.key === 'post') {
        const data = await funnelService.searchPosts(query, 30, dateRange)
        results = data.map(r => ({
          value: r.id,
          label: `${r.caption} — @${r.author}`,
        }))
      } else if (selectedDimension.key === 'brand') {
        const data = await funnelService.searchBrands(query)
        results = data.map(r => ({ value: r.id, label: r.name }))
      } else if (selectedDimension.key === 'team') {
        const data = await funnelService.searchBrands(query)
        results = data.map(r => ({ value: r.id, label: r.name }))
      } else if (selectedDimension.key === 'item') {
        const data = await funnelService.searchItems(query)
        results = data.map(r => ({ value: r.id, label: r.name }))
      }
      setDimensionValues(results)
    } catch {
      setDimensionValues([])
    } finally {
      setIsSearching(false)
    }
  }, [selectedDimension, dateRange])

  const closeDimensionModal = useCallback(() => {
    setIsDimensionModalOpen(false)
  }, [])

  // -------------------------------------------------------------------------
  // Load Persona flow
  // -------------------------------------------------------------------------

  const openLoadPersona = useCallback(() => {
    setIsLoadPersonaOpen(true)
  }, [])

  const closeLoadPersona = useCallback(() => {
    setIsLoadPersonaOpen(false)
  }, [])

  const selectPersona = useCallback((persona: { layers?: ReadonlyArray<{ dimension: string; dimensionLabel?: string; values: (string | number)[]; valueLabels: string[] }> }) => {
    if (persona.layers && persona.layers.length > 0) {
      const filters: DiscoveryFilter[] = persona.layers.map(layer => ({
        dimension: layer.dimension,
        dimensionLabel: layer.dimensionLabel || layer.dimension,
        values: layer.values,
        valueLabels: resolveValueLabels(
          layer.dimension,
          layer.values,
          layer.valueLabels,
          filterOptions,
        ),
      }))
      loadFilters(filters)
    }
    setIsLoadPersonaOpen(false)
  }, [loadFilters, filterOptions])

  const deletePersona = useCallback(async (id: number) => {
    setIsDeletingPersona(true)
    try {
      await doDeletePersona(id)
    } finally {
      setIsDeletingPersona(false)
    }
  }, [doDeletePersona])

  // Dimension counts stub
  const requestDimensionCounts = useCallback(async () => {
    return undefined
  }, [])

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------
  return {
    isLoadPersonaOpen,
    personas: personas as any,
    isLoadingPersonas,
    isDeletingPersona,
    openLoadPersona,
    closeLoadPersona,
    selectPersona,
    deletePersona,
    isDimensionModalOpen,
    isValueModalOpen,
    selectedDimension,
    dimensionValues,
    isLoadingValues,
    isSearchMode,
    isSearching,
    usedDimensions,
    initialSelectedValues,
    openAddFilter,
    selectDimension,
    applyValues,
    closeValueModal,
    closeDimensionModal,
    handleSearch,
    requestDimensionCounts,
  }
}
